import I18n from 'I18n';
import { getIsPropertyOnlyToken } from 'rich-text-lib/utils/properties';
import { PropertyCategories } from 'rich-text-lib/constants/properties';
const OWNER_CATEGORY = 'owner';
const SITE_SETTINGS_CATEGORY = 'site_settings';
const ALL_TOKEN_PATTERN = /{{\s*(company|contact|deal|line_item|owner|quote|site_settings|ticket)\.(\w+)(?:\.(\w+))?\s*}}/g;
const PROPERTY_ONLY_TOKEN_PATTERN = /{{\s*(subscription_name)?\s*}}/g;
const TOKEN_PATTERN_WITH_DEFAULT = /{{\s*personalization_token\(\s*["']([^\\.\s]+)\.(\w+)\.?(\w+)?["']\s*,\s*["'](.*?)["']\s*\)\s*}}/g;
const HUBL_PATH = /{{-?\s*([a-zA-Z0-9_()\-[\]]+(?:[.][a-zA-Z0-9_()\-[\]]+)*)\s*-?}}/g;
const HUBL_PERSONALIZATION_PATTERN = /{{\s*personalization_token\(\s*["']([a-zA-Z0-9_()\-[\]]+(?:[.][a-zA-Z0-9_()\-[\]]+)*)["']\s*,\s*["'](.*?)["']{1}\s*\)\s*}}/g;
function escapeRegex(escaping) {
  return escaping.replace(new RegExp(/[[]/g), '\\$&');
}
function escapeParentheses(escaping) {
  return escaping.replace(new RegExp(/\(([^()]*)\)/g), (__wrapper, el) => `\\(${el}\\)`);
}
const getTokenCategories = ({
  hideSiteSettingsCategory,
  supportedCrmObjectFQNs
}) => {
  const tokens = [OWNER_CATEGORY];
  if (Array.isArray(supportedCrmObjectFQNs)) {
    tokens.push(...supportedCrmObjectFQNs.map(fqn => escapeRegex(escapeParentheses(fqn))));
  }
  if (!hideSiteSettingsCategory) {
    tokens.push(SITE_SETTINGS_CATEGORY);
  }
  return tokens.filter((value, index, self) => {
    return self.indexOf(value) === index;
  }).sort();
};
const getPrefixes = prefixes => {
  if (typeof prefixes !== 'object') return [];
  return Object.values(prefixes).map(prefix => escapeRegex(prefix));
};
const verifyCategory = (prefixes, category) => {
  if (!prefixes || typeof prefixes !== 'object') return category;
  let validCategory = category;
  Object.keys(prefixes).forEach(key => {
    const value = prefixes[key];
    if (value === category) {
      validCategory = key;
    }
  });
  return validCategory;
};
const getTokenPattern = ({
  prefixes,
  hideSiteSettingsCategory,
  supportedCrmObjectFQNs
}) => {
  if (prefixes === undefined && hideSiteSettingsCategory === undefined && supportedCrmObjectFQNs === undefined) {
    return ALL_TOKEN_PATTERN;
  }
  const regexPatterns = getTokenCategories({
    hideSiteSettingsCategory,
    supportedCrmObjectFQNs
  }).join('|');
  const regexPrefixes = getPrefixes(prefixes).join('|');
  return new RegExp(`{{\\s*(${regexPatterns}${regexPrefixes.length !== 0 ? `|${regexPrefixes}` : ''})\\.(\\w+)(?:\\.(\\w+))?\\s*}}`, 'g');
};
const getTokenPatternWithDefault = ({
  prefixes,
  hideSiteSettingsCategory,
  supportedCrmObjectFQNs
}) => {
  if (prefixes === undefined && hideSiteSettingsCategory === undefined && supportedCrmObjectFQNs === undefined) {
    return TOKEN_PATTERN_WITH_DEFAULT;
  }
  const regexPatterns = getTokenCategories({
    hideSiteSettingsCategory,
    supportedCrmObjectFQNs
  }).join('|');
  const regexPrefixes = getPrefixes(prefixes).join('|');
  return new RegExp(`{{\\s*personalization_token\\(\\s*["']{1}(${regexPatterns}${regexPrefixes.length !== 0 ? `|${regexPrefixes}` : ''})\\.(\\w+)(?:\\.(\\w+))?["']{1}\\s*,\\s*["']{1}(.*?)["']{1}\\s*\\)\\s*}}`, 'g');
};
const getPropertyOnlyTokenPattern = ({
  prefixes
}) => {
  if (prefixes === undefined) return PROPERTY_ONLY_TOKEN_PATTERN;
  const regexPrefixes = getPrefixes(prefixes).join('|');
  return new RegExp(`{{\\s*(subscription_name${regexPrefixes.length !== 0 ? `|${regexPrefixes}` : ''})?\\s*}}`, 'g');
};
export const getTokens = (value = '', {
  prefixes,
  hideSiteSettingsCategory,
  supportedCrmObjectFQNs
} = {}) => {
  const tokens = {};
  [getTokenPattern({
    prefixes,
    hideSiteSettingsCategory,
    supportedCrmObjectFQNs
  }), getTokenPatternWithDefault({
    prefixes,
    hideSiteSettingsCategory,
    supportedCrmObjectFQNs
  }), getPropertyOnlyTokenPattern({
    prefixes
  })].forEach(tokenPattern => {
    // create a new copy, as RegExp is a stateful operation
    const tokenPatternCopy = new RegExp(tokenPattern);
    let match = tokenPatternCopy.exec(value);
    while (match !== null) {
      if (tokens[match.index]) {
        continue;
      }
      const [fullToken, category, property, subProperty, defaultValue] = match;
      const validCategory = verifyCategory(prefixes, category);
      tokens[match.index] = {
        fullToken,
        category: validCategory,
        property,
        subProperty,
        defaultValue,
        length: fullToken.length,
        index: match.index
      };
      match = tokenPatternCopy.exec(value);
    }
  });
  return Object.values(tokens);
};
export const getHubLPaths = (value = '') => {
  const tokens = {};
  [HUBL_PERSONALIZATION_PATTERN, HUBL_PATH].forEach(tokenPattern => {
    // create a new copy, as RegExp is a stateful operation
    const tokenPatternCopy = new RegExp(tokenPattern);
    let match = tokenPatternCopy.exec(value);
    while (match !== null) {
      if (tokens[match.index]) {
        continue;
      }
      const [fullToken, path] = match;
      tokens[match.index] = {
        fullToken,
        path,
        length: fullToken.length,
        index: match.index
      };
      match = tokenPatternCopy.exec(value);
    }
  });
  return Object.values(tokens);
};

/**
 *
 * @param {String} category token category
 * @returns {String} icon code for custom font
 */
export const getTokenIconFromCategory = (category = '') => {
  switch (category.toLowerCase()) {
    case PropertyCategories.CONTACT:
      return 'contacts';
    case PropertyCategories.COMPANY:
      return 'companies';
    case PropertyCategories.DEAL:
      return 'deals';
    case PropertyCategories.QUOTE:
      return 'salesQuote';
    default:
      return 'hubDB';
  }
};

/**
 *
 * @param {Object} properties
 * @returns {String} Translated property label
 */
export const getPlaceholderLabel = properties => {
  const {
    property,
    category,
    translatedLabel
  } = properties;
  let labelPath;
  let label;
  const isPropertyOnlyToken = getIsPropertyOnlyToken(category, property);
  if (isPropertyOnlyToken) {
    labelPath = property;
    // This I18n message is only needed for site settings properties
    // i18n-lint-disable-next-line require-i18n-message
    label = I18n.text(`data-token-views.propertyOnlyTokens.${labelPath}`);
  } else if (category) {
    label = translatedLabel;
  } else {
    label = property;
  }
  return label;
};